import { formatAuditAction, formatAuditContact, matchesAuditSearch } from '@/lib/auditDisplay';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, User, Shield, ShieldAlert, Trash2, Edit, Plus, Building2, RefreshCw, MapPin, AlertTriangle, Info, AlertCircle, X, Eye, FileText, Zap, CheckCircle, XCircle, Clock, SkipForward } from 'lucide-react';
// Note: Some icons (Trash2, Edit) retained for action detection in getIcon(), not for edit/delete UI
import { SearchInput } from '@/components/ui/search-input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { AdminDataTable, Column } from '@/components/settings/AdminDataTable';
import { getGlobalAuditLogs, getAuditLogsByResourceTypes, getAdminCompanys, getAuditLogDetails } from '@/lib/adminApi';
import { formatAuditTimestamp, formatUserRole } from '@/lib/utils';
import { useSuperadminNotificationsSafe } from '@/contexts/SuperadminNotificationContext';
import type { AuditLog, AuditLogSubItem } from '@/types';

// Resource type labels for display
const RESOURCE_TYPE_LABELS: Record<string, string> = {
  system_settings: 'System Settings',
  rate_profile: 'Rate Profiles',
  rate_assignment: 'Rate Assignments',
  utility_provider: 'Utility Providers',
  company: 'Companies',
  group: 'Groups',
  unit: 'Units',
  user: 'Users',
};

// Minimal company shape used by the audit filter dropdown
interface AuditCompany {
  id: string;
  name: string;
  industry?: string;
}

// Helper to group companies by industry for organized dropdowns
const groupCompaniesByIndustry = (companies: AuditCompany[]) => {
  const byIndustry: Record<string, AuditCompany[]> = {};
  companies.forEach(company => {
    const industry = company.industry || 'Other';
    if (!byIndustry[industry]) byIndustry[industry] = [];
    byIndustry[industry].push(company);
  });
  // Sort industries alphabetically, but put 'Other' at the end
  const sortedIndustries = Object.keys(byIndustry).sort((a, b) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });
  return { byIndustry, sortedIndustries };
};

export function SettingsGlobalAudit() {
  // URL parameters for resource type filtering
  const [searchParams, setSearchParams] = useSearchParams();
  const urlResourceTypes = searchParams.get('resource_types');
  const [selectedResourceTypes, setSelectedResourceTypes] = useState<string[]>(() => {
    return urlResourceTypes ? urlResourceTypes.split(',').filter(Boolean) : [];
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<string>('all');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [companies, setCompanies] = useState<AuditCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // Expanded row details state
  const [logDetails, setLogDetails] = useState<Map<string, AuditLogSubItem[]>>(new Map());
  const [loadingDetails, setLoadingDetails] = useState<Set<string>>(new Set());

  // Get notification context for live indicator
  const notifications = useSuperadminNotificationsSafe();
  const isEnabled = notifications?.isEnabled || false;
  const recentNotifications = notifications?.recentNotifications || [];

  // Fetch logs function (reusable for refresh)
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      // Use resource type filter if set, otherwise fetch all
      if (selectedResourceTypes.length > 0) {
        const result = await getAuditLogsByResourceTypes({
          types: selectedResourceTypes,
          limit: 500,
        });
        setLogs(result.logs);
      } else {
        const logsData = await getGlobalAuditLogs();
        setLogs(logsData.logs);
      }
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedResourceTypes]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch logs based on resource type filter
        const logsPromise = selectedResourceTypes.length > 0
          ? getAuditLogsByResourceTypes({ types: selectedResourceTypes, limit: 500 })
          : getGlobalAuditLogs();

        const [logsData, companiesData] = await Promise.all([
          logsPromise,
          getAdminCompanys()
        ]);
        setLogs(logsData.logs);
        setCompanies(companiesData.companies || []);
        setLastRefresh(new Date());
      } catch (error) {
        console.error('Failed to fetch global audit data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedResourceTypes]);

  // Auto-refresh when new notifications come in
  useEffect(() => {
    if (recentNotifications.length > 0 && lastRefresh) {
      // Check if there are new notifications since last refresh
      const newestNotificationTime = new Date(recentNotifications[0]?.timestamp);
      if (newestNotificationTime > lastRefresh) {
        fetchLogs();
      }
    }
  }, [recentNotifications, lastRefresh, fetchLogs]);

  // Handle row expansion - lazy load details
  const handleRowExpand = useCallback(async (log: AuditLog, isExpanded: boolean) => {
    if (!isExpanded || logDetails.has(log.id)) return;

    // Mark as loading
    setLoadingDetails(prev => new Set(prev).add(log.id));

    try {
      const { details } = await getAuditLogDetails(log.id);
      setLogDetails(prev => new Map(prev).set(log.id, details));
    } catch (error) {
      console.error('Failed to fetch audit log details:', error);
      // Set empty array to prevent re-fetching on error
      setLogDetails(prev => new Map(prev).set(log.id, []));
    } finally {
      setLoadingDetails(prev => {
        const next = new Set(prev);
        next.delete(log.id);
        return next;
      });
    }
  }, [logDetails]);

  // Check if a log is loading details
  const isDetailsLoading = useCallback((log: AuditLog) => {
    return loadingDetails.has(log.id);
  }, [loadingDetails]);

  // Get action status icon and color
  const getActionStatusIcon = (status?: string) => {
    switch (status) {
      case 'success': return <CheckCircle className="w-3.5 h-3.5 text-success" />;
      case 'failed': return <XCircle className="w-3.5 h-3.5 text-error" />;
      case 'pending': return <Clock className="w-3.5 h-3.5 text-warning" />;
      case 'skipped': return <SkipForward className="w-3.5 h-3.5 text-muted-foreground" />;
      default: return <Zap className="w-3.5 h-3.5 text-blue-400" />;
    }
  };

  // Render expanded row content
  const renderExpandedRow = useCallback((log: AuditLog) => {
    const details = logDetails.get(log.id) || [];

    if (details.length === 0) {
      return (
        <div className="text-sm text-[#8FB560]/70 py-1">
          No additional details available for this action.
        </div>
      );
    }

    const fieldChanges = details.filter(d => d.type === 'field_change');
    const consequentActions = details.filter(d => d.type === 'consequent_action');

    return (
      <div className="space-y-4">
        {/* Field Changes */}
        {fieldChanges.length > 0 && (
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold text-[#8FB560] mb-2 uppercase tracking-wide">
              <FileText className="w-3.5 h-3.5" />
              Field Changes ({fieldChanges.length})
            </div>
            <div className="bg-background/50 rounded-md border border-[#8FB560]/10 divide-y divide-[#8FB560]/10">
              {fieldChanges.map((change) => (
                <div key={change.id} className="flex items-center gap-3 text-sm px-3 py-2">
                  <span className="text-foreground/70 font-medium min-w-[140px]">{change.fieldName}</span>
                  {change.oldValue !== null && change.oldValue !== undefined ? (
                    <div className="flex items-center gap-2 flex-1">
                      <span className="text-red-400/80 line-through text-xs bg-red-500/10 px-2 py-0.5 rounded">
                        {String(change.oldValue) || '(empty)'}
                      </span>
                      <span className="text-[#8FB560]">→</span>
                      <span className="text-[#8FB560] font-medium text-xs bg-[#8FB560]/15 px-2 py-0.5 rounded">
                        {String(change.newValue) || '(empty)'}
                      </span>
                    </div>
                  ) : (
                    <span className="text-foreground font-medium">{change.newValue}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Consequent Actions */}
        {consequentActions.length > 0 && (
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold text-[#8FB560] mb-2 uppercase tracking-wide">
              <Zap className="w-3.5 h-3.5" />
              Related Actions ({consequentActions.length})
            </div>
            <div className="bg-background/50 rounded-md border border-[#8FB560]/10 divide-y divide-[#8FB560]/10">
              {consequentActions.map((action) => (
                <div key={action.id} className="flex items-center gap-2 text-sm px-3 py-2">
                  {getActionStatusIcon(action.actionStatus)}
                  <span className="text-foreground">{action.actionDescription}</span>
                  {action.actionStatus && action.actionStatus !== 'success' && (
                    <Badge className={`text-xs px-1.5 py-0 ${
                      action.actionStatus === 'failed' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                      action.actionStatus === 'pending' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                      'bg-muted text-muted-foreground border border-border'
                    }`}>
                      {action.actionStatus}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }, [logDetails]);

  // All icons use neutral gray - colored badges are the sole color indicator
  const getIcon = (action: string) => {
    const iconClass = "w-4 h-4 text-zinc-400";
    if (action.includes('Login') || action.includes('Logout')) return <User className={iconClass} />;
    if (action.includes('Delete')) return <Trash2 className={iconClass} />;
    if (action.includes('Create') || action.includes('Invite')) return <Plus className={iconClass} />;
    if (action.includes('Update')) return <Edit className={iconClass} />;
    if (action.includes('Block')) return <ShieldAlert className={iconClass} />;
    if (action.includes('Check')) return <CheckCircle className={iconClass} />;
    if (action.includes('Company') || action.includes('Account')) return <Building2 className={iconClass} />;
    if (isSystemAction(action)) return <Shield className={iconClass} />;
    return <Shield className={iconClass} />;
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-error bg-zinc-800/60';
      case 'warning': return 'text-warning bg-zinc-800/60';
      default: return 'text-muted-foreground bg-zinc-800/60';
    }
  };

  // Check if action is a system-related action
  const isSystemAction = (action: string) => {
    const systemKeywords = ['system', 'settings', 'config', 'profile', 'preference'];
    const lowerAction = action?.toLowerCase() || '';
    return systemKeywords.some(keyword => lowerAction.includes(keyword));
  };

  // Format action - convert SNAKE_CASE to Title Case
  // For system actions, removes redundant "System" from title since status badge shows "System"
  const formatAction = (action: string) => {
    if (!action) return '';
    let formatted = formatAuditAction(action);

    // For system-related actions, remove "System" from the title (status badge shows it)
    if (isSystemAction(action)) {
      formatted = formatted.replace(/\bSystem\s*/gi, '').trim();
    }

    return formatted;
  };

  // Role badge styling with distinct colors
  const getRoleBadgeColor = (role: string) => {
    switch (role?.toLowerCase()) {
      case 'superadmin': return 'bg-[#8FB560]/10 text-[#8FB560]';
      case 'admin': return 'bg-blue-500/10 text-blue-400';
      case 'group_admin': return 'bg-purple-500/10 text-purple-400';
      case 'team_admin': return 'bg-cyan-500/10 text-cyan-400';
      case 'user': return 'bg-zinc-500/10 text-zinc-400';
      default: return 'bg-zinc-800/60 text-zinc-300';
    }
  };

  // Extract status from details/action with multiple states
  const getStatus = (log: AuditLog): { type: 'success' | 'created' | 'updated' | 'deleted' | 'system' | 'failed' | 'blocked' | 'warning' | 'timeout' | 'pending' | 'default'; label: string } => {
    const action = log.action?.toLowerCase() || '';
    const details = log.details?.toLowerCase() || '';

    // Priority 1: Failed login - check BEFORE success to catch "Failed Login" action
    if (action.includes('failed') && action.includes('login')) {
      return { type: 'failed', label: 'Failed' };
    }
    // Priority 2: Login/logout actions - these are user actions, not system
    if (action.includes('logout') || details.includes('logged out')) {
      return { type: 'success', label: 'Success' };
    }
    if (action.includes('login') && !details.includes('failed')) {
      return { type: 'success', label: 'Success' };
    }

    // Priority 2: Check-in/check-out are user actions
    if (action.includes('check_in') || action.includes('check_out') || action.includes('checkin') || action.includes('checkout')) {
      return { type: 'success', label: 'Success' };
    }

    // Priority 3: Delete actions
    if (action.includes('delete') || details.includes('deleted') || details.includes('removed')) {
      return { type: 'deleted', label: 'Deleted' };
    }

    // Priority 4: Create/invite actions
    if (action.includes('create') || action.includes('invite') || details.includes('created') || details.includes('invited')) {
      return { type: 'created', label: 'Created' };
    }

    // Priority 5: Blocked
    if (action.includes('block') || details.includes('blocked')) {
      return { type: 'blocked', label: 'Blocked' };
    }

    // Priority 6: Timeout
    if (details.includes('timed out') || details.includes('timeout')) {
      return { type: 'timeout', label: 'Timed Out' };
    }

    // Priority 7: Failed (critical severity)
    if (details.includes('failed') || log.severity === 'critical') {
      return { type: 'failed', label: 'Failed' };
    }

    // Priority 8: Error/warning
    if (details.includes('error')) {
      return { type: 'warning', label: 'Warning' };
    }

    // Priority 9: System updates (before generic updated check)
    if (isSystemAction(log.action) && (action.includes('update') || details.includes('updated'))) {
      return { type: 'system', label: 'System' };
    }

    // Priority 10: General updates
    if (action.includes('update') || details.includes('updated')) {
      return { type: 'updated', label: 'Updated' };
    }

    // Priority 11: View actions are system
    if (action.includes('view') || action.includes('read') || action.includes('list')) {
      return { type: 'system', label: 'System' };
    }

    // Priority 12: Info severity (generic success)
    if (log.severity === 'info') {
      return { type: 'system', label: 'System' };
    }

    return { type: 'default', label: '—' };
  };

  // Get badge color based on status type
  const getStatusBadgeClass = (type: string) => {
    switch (type) {
      case 'success': return 'bg-success/10 text-success';
      case 'created': return 'bg-emerald-500/10 text-emerald-400';
      case 'updated': return 'bg-blue-500/10 text-blue-400';
      case 'deleted': return 'bg-red-500/10 text-red-400';
      case 'system': return 'bg-purple-500/10 text-purple-400';
      case 'failed': return 'bg-error/10 text-error';
      case 'blocked': return 'bg-orange-500/10 text-orange-400';
      case 'warning': return 'bg-yellow-500/10 text-yellow-400';
      case 'timeout': return 'bg-zinc-500/10 text-zinc-400';
      case 'pending': return 'bg-cyan-500/10 text-cyan-400';
      default: return 'bg-zinc-800/60 text-zinc-500';
    }
  };

  // Format IP - show "localhost" for localhost addresses
  const formatIp = (ip: string) => {
    if (!ip || ip === '::1' || ip === '127.0.0.1' || ip === 'localhost') return 'localhost';
    return ip;
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = matchesAuditSearch(log, searchTerm);
    const matchesCompany = selectedCompany === 'all' || log.company === selectedCompany;
    const matchesSeverity = selectedSeverity === 'all' || log.severity === selectedSeverity;

    // Date range filtering
    const logDate = new Date(log.timestamp);
    const matchesFromDate = !fromDate || logDate >= fromDate;
    const matchesToDate = !toDate || logDate <= new Date(toDate.getTime() + 24 * 60 * 60 * 1000 - 1); // Include full day

    return matchesSearch && matchesCompany && matchesSeverity && matchesFromDate && matchesToDate;
  });

  // Column definitions for AdminDataTable
  const columns: Column<AuditLog>[] = useMemo(() => [
    {
      key: 'action',
      label: 'Event',
      sortable: true,
      render: (log) => (
        <div className="flex items-center gap-2 min-w-[100px]">
          <div className={`p-1.5 rounded-md flex-shrink-0 ${getSeverityColor(log.severity)}`}>
            {getIcon(log.action)}
          </div>
          <span className="font-medium text-white text-xs whitespace-nowrap">{formatAction(log.action)}</span>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (log) => {
        const status = getStatus(log);
        return (
          <Badge className={`${getStatusBadgeClass(status.type)} border-0 text-xs px-2 py-0.5 whitespace-nowrap`}>
            {status.label}
          </Badge>
        );
      },
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (log) => (
        <span className="text-white text-xs whitespace-nowrap">
          {log.name || '—'}
        </span>
      ),
    },
    {
      key: 'user',
      label: 'Contact',
      sortable: true,
      render: (log) => {
        const contact = formatAuditContact(log);
        return (
          <span className="text-muted-foreground text-xs whitespace-nowrap" title={contact}>
            {contact || '—'}
          </span>
        );
      },
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      render: (log) => (
        <Badge className={`${getRoleBadgeColor(log.role)} border-0 text-xs px-2 py-0.5 whitespace-nowrap rounded-md`}>
          {formatUserRole(log.role)}
        </Badge>
      ),
    },
    {
      key: 'company',
      label: 'Company',
      sortable: true,
      render: (log) => (
        <div className="min-w-[100px] max-w-[160px]">
          <span className="text-zinc-300 text-xs truncate block" title={log.company || 'Unknown'}>
            {log.company || 'Unknown'}
          </span>
        </div>
      ),
    },
    {
      key: 'ip',
      label: 'IP',
      render: (log) => (
        <span className="text-muted-foreground font-mono text-xs whitespace-nowrap">{formatIp(log.ip)}</span>
      ),
    },
    {
      key: 'location',
      label: 'Location',
      sortable: true,
      render: (log) => (
        <div className="flex items-start gap-1 text-muted-foreground min-w-[80px] max-w-[140px]">
          {log.location && <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" />}
          <span
            className="text-[11px] leading-tight line-clamp-2"
            style={{ wordBreak: 'break-word' }}
            title={log.location || ''}
          >
            {log.location || '—'}
          </span>
        </div>
      ),
    },
    {
      key: 'timestamp',
      label: 'Time',
      sortable: true,
      render: (log) => (
        <span className="text-muted-foreground text-xs whitespace-nowrap">{formatAuditTimestamp(log.timestamp)}</span>
      ),
    },
  ], [filteredLogs]);

  // Group companies by industry for the dropdown
  const { byIndustry, sortedIndustries } = groupCompaniesByIndustry(companies);

  // Security metrics calculations
  const now = new Date();
  const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const last7d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const failedLogins = logs.filter(log =>
    log.action.toLowerCase().includes('failed') &&
    log.action.toLowerCase().includes('login')
  );

  const failedLogins24h = failedLogins.filter(log => new Date(log.timestamp) >= last24h);
  const failedLogins7d = failedLogins.filter(log => new Date(log.timestamp) >= last7d);

  // Top IPs with failed logins (last 7 days)
  const ipCounts = failedLogins7d.reduce((acc, log) => {
    const ip = log.ip || 'Unknown';
    acc[ip] = (acc[ip] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const topFailedIPs = Object.entries(ipCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([ip, count]) => ({ ip, count }));

  // Quick filter for failed logins
  const showFailedLoginsOnly = () => {
    setSearchTerm('failed login');
    setSelectedSeverity('all');
    setSelectedCompany('all');
  };

  // CSV Export function
  const exportToCSV = () => {
    if (filteredLogs.length === 0) return;

    // Define CSV headers
    const headers = ['Timestamp', 'Action', 'Status', 'Name', 'Email', 'Role', 'Company', 'Severity', 'IP', 'Location', 'Details'];

    // Convert logs to CSV rows
    const rows = filteredLogs.map(log => {
      const status = getStatus(log);

      return [
        new Date(log.timestamp).toISOString(),
        formatAction(log.action),
        status.label,
        log.name || '',
        log.user,
        log.role,
        log.company || '',
        log.severity,
        formatIp(log.ip),
        log.location || '',
        log.details?.replace(/"/g, '""') || '' // Escape quotes in details
      ];
    });

    // Build CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().split('T')[0];
    link.href = URL.createObjectURL(blob);
    link.download = `audit-logs-${timestamp}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-shrink-0">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-bold text-white whitespace-nowrap">Global Audit Logs</h1>
              {/* Live Indicator */}
              {isEnabled && (
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-success/10 border border-success/20">
                  <span className="h-2 w-2 rounded-full bg-success" />
                  <span className="text-xs font-medium text-success">Live</span>
                </div>
              )}
              {lastRefresh && (
                <span className="text-xs text-muted-foreground">
                  Last updated {lastRefresh.toLocaleTimeString()}
                </span>
              )}
            </div>
            <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">
              Cross-company system activity and security events
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="border-border text-foreground hover:text-foreground whitespace-nowrap"
              onClick={fetchLogs}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <div className="flex items-center gap-2">
              <DatePicker
                date={fromDate}
                onDateChange={setFromDate}
                placeholder="From date"
                className="w-[130px] h-9"
              />
              <span className="text-muted-foreground">–</span>
              <DatePicker
                date={toDate}
                onDateChange={setToDate}
                placeholder="To date"
                className="w-[130px] h-9"
              />
              {(fromDate || toDate) && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9"
                  onClick={() => {
                    setFromDate(undefined);
                    setToDate(undefined);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Button
              variant="outline"
              className="border-border text-foreground hover:text-foreground whitespace-nowrap flex-1 sm:flex-initial"
              onClick={exportToCSV}
              disabled={filteredLogs.length === 0}
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
      </div>

      {/* Resource Type Filter Badge */}
      {selectedResourceTypes.length > 0 && (
        <div className="flex items-center gap-2 bg-[#8FB560]/10 border border-[#8FB560]/30 rounded-lg px-4 py-2">
          <FileText className="w-4 h-4 text-[#8FB560]" />
          <span className="text-sm text-[#8FB560] font-medium">
            Showing: {selectedResourceTypes.map(t => RESOURCE_TYPE_LABELS[t] || t).join(', ')}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-[#8FB560] hover:text-[#8FB560]/80 hover:bg-[#8FB560]/10"
            onClick={() => {
              setSelectedResourceTypes([]);
              setSearchParams(prev => {
                const newParams = new URLSearchParams(prev);
                newParams.delete('resource_types');
                return newParams;
              });
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <SearchInput
            placeholder="Search logs across all companies..."
            value={searchTerm}
            onValueChange={setSearchTerm}
            className="bg-background border-border"
          />
        </div>
        <Select value={selectedCompany} onValueChange={setSelectedCompany}>
          <SelectTrigger className="w-[200px] bg-background border-border">
            <SelectValue placeholder="All Companies" />
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            <SelectItem value="all">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                <span>All Companies</span>
              </div>
            </SelectItem>
            {sortedIndustries.map(industry => (
              <div key={industry}>
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {industry}
                </div>
                {byIndustry[industry].map((company) => (
                  <SelectItem key={company.id} value={company.name}>
                    {company.name}
                  </SelectItem>
                ))}
              </div>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedSeverity} onValueChange={setSelectedSeverity}>
          <SelectTrigger className="w-[140px] bg-background border-border">
            <SelectValue placeholder="All Severity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-muted-foreground" />
                <span>All Severity</span>
              </div>
            </SelectItem>
            <SelectItem value="info">
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-400" />
                <span>Info</span>
              </div>
            </SelectItem>
            <SelectItem value="warning">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-warning" />
                <span>Warning</span>
              </div>
            </SelectItem>
            <SelectItem value="critical">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-error" />
                <span>Critical</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-muted-foreground text-sm">Total Events</div>
          <div className="text-2xl font-bold text-white mt-1">{filteredLogs.length}</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-muted-foreground text-sm">Critical Events</div>
          <div className="text-2xl font-bold text-error mt-1">
            {filteredLogs.filter(log => log.severity === 'critical').length}
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-muted-foreground text-sm">Warnings</div>
          <div className="text-2xl font-bold text-warning mt-1">
            {filteredLogs.filter(log => log.severity === 'warning').length}
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="text-muted-foreground text-sm">Companies Active</div>
          <div className="text-2xl font-bold text-[#8FB560] mt-1">
            {new Set(logs.map(log => log.company)).size}
          </div>
        </div>
      </div>

      {/* Security Alerts Panel */}
      {failedLogins7d.length > 0 && (
        <div className="bg-card border border-error/20 rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-error" />
              <h3 className="font-semibold text-white">Security Alerts</h3>
              <Badge className="bg-error/10 text-error border-0 text-xs">
                {failedLogins7d.length} failed {failedLogins7d.length === 1 ? 'login' : 'logins'}
              </Badge>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-error/30 text-error hover:bg-error/10"
              onClick={showFailedLoginsOnly}
            >
              <Eye className="w-4 h-4 mr-2" />
              View All
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Failed Login Counts */}
            <div className="bg-background/50 rounded-lg p-3">
              <div className="text-muted-foreground text-xs mb-2">Failed Logins</div>
              <div className="flex items-baseline gap-4">
                <div>
                  <span className="text-2xl font-bold text-error">{failedLogins24h.length}</span>
                  <span className="text-xs text-muted-foreground ml-1">24h</span>
                </div>
                <div>
                  <span className="text-xl font-semibold text-error/70">{failedLogins7d.length}</span>
                  <span className="text-xs text-muted-foreground ml-1">7d</span>
                </div>
              </div>
            </div>

            {/* Top IPs */}
            <div className="bg-background/50 rounded-lg p-3 md:col-span-2">
              <div className="text-muted-foreground text-xs mb-2">Top IPs with Failed Attempts (7d)</div>
              {topFailedIPs.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {topFailedIPs.map(({ ip, count }) => (
                    <Badge
                      key={ip}
                      className="bg-error/10 text-error border border-error/20 text-xs font-mono cursor-pointer hover:bg-error/20"
                      onClick={() => setSearchTerm(ip)}
                    >
                      {formatIp(ip)} <span className="ml-1 opacity-70">×{count}</span>
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-muted-foreground text-sm">No failed login attempts</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Logs Table */}
      <AdminDataTable
        data={filteredLogs}
        columns={columns}
        loading={loading}
        emptyMessage="No audit logs found matching your criteria."
        tableId="global-audit-logs"
        expandable={true}
        canExpand={(log) => log.hasDetails === true}
        onRowExpand={handleRowExpand}
        expandedRowRender={renderExpandedRow}
        expandedRowLoading={isDetailsLoading}
      />
    </div>
  );
}
